import alert from '@jetbrains/ring-ui/components/alert-service/alert-service'
import Input from '@jetbrains/ring-ui/components/input/input'
import classNames from 'classnames'
import * as React from 'react'
import type {SyntheticEvent} from 'react'
import {useMutation} from 'react-relay'
import {useNavigate} from 'react-router-dom'

import {getAgentPoolHref} from '../../../../../routes'
import {getAgentPools} from '../../../../../selectors'
import {stringifyId, toAgentPoolId} from '../../../../../types'
import {notNull} from '../../../../../utils/guards'
import {objectValues} from '../../../../../utils/object'
import type {AgentPoolPageCreateAgentPoolMutation} from '../../AgentPoolPage/__generated__/AgentPoolPageCreateAgentPoolMutation.graphql'
import type {AgentPoolPageUpdateAgentPoolMutation} from '../../AgentPoolPage/__generated__/AgentPoolPageUpdateAgentPoolMutation.graphql'
import {createAgentPool, updateAgentPool} from '../../AgentPoolPage/AgentPoolPage.queries'
import type {AgentPool} from '../../AgentPoolPage/AgentPoolPage.types'
import CommonForm from '../CommonForm/CommonForm'

import DeleteAgentPoolButton from './DeleteAgentPoolButton/DeleteAgentPoolButton'

import styles from './EditAgentPoolForm.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  onUpdate: (arg0: void) => void
  onRemove?: (arg0: void) => void
  onClose: (arg0: void) => void
  agentPool?: AgentPool
}
const JAVA_MAX_INTEGER = 2147483647
const MAX_AGENT_POOL_NAME_LENGTH = 190
export default function EditAgentPoolForm({
  onUpdate,
  onRemove,
  onClose,
  agentPool,
}: Props): React.ReactElement {
  const defaultName = agentPool?.name != null ? agentPool.name : ''
  const [name, setName] = React.useState(defaultName)
  const [nameError, setNameError] = React.useState('')
  const defaultMaxAgents =
    agentPool?.maxAgentsNumber != null && agentPool.maxAgentsNumber !== -1
      ? String(agentPool.maxAgentsNumber)
      : ''
  const [maxAgents, setMaxAgents] = React.useState<string>(defaultMaxAgents)
  const [maxAgentsError, setMaxAgentsError] = React.useState('')
  const formRef = React.useRef()
  const agentPools = useAppSelector(state => getAgentPools(state))
  const navigate = useNavigate()
  const clearErrors = React.useCallback(() => {
    setNameError('')
    setMaxAgentsError('')
  }, [setNameError, setMaxAgentsError])
  const clearFields = React.useCallback(() => {
    setName(defaultName)
    setMaxAgents(defaultMaxAgents)
  }, [defaultMaxAgents, defaultName])

  const [commitCreate] = useMutation<AgentPoolPageCreateAgentPoolMutation>(createAgentPool)
  const [commitUpdate] = useMutation<AgentPoolPageUpdateAgentPoolMutation>(updateAgentPool)

  const handleSubmit = React.useCallback(() => {
    clearErrors()

    if (name.trim() === '') {
      setNameError('The name should not be empty')
      return Promise.reject()
    }

    if (
      objectValues(agentPools)
        .filter(notNull)
        .find(
          ({id: poolId, name: poolName}) =>
            (agentPool == null || agentPool.rawId !== stringifyId(poolId)) && poolName === name,
        )
    ) {
      setNameError(`Agent pool "${name}" already exists`)
      return Promise.reject()
    }

    let maxAgentsNumber: number | null = maxAgents === '' ? null : parseInt(maxAgents, 10)

    if (maxAgentsNumber != null && maxAgentsNumber < 1) {
      setMaxAgentsError('Maximum number of agents should be greater than zero')
      return Promise.reject()
    }

    if (maxAgentsNumber == null || maxAgentsNumber > JAVA_MAX_INTEGER) {
      maxAgentsNumber = -1
    }

    if (agentPool == null) {
      return new Promise((resolve, reject) => {
        commitCreate({
          variables: {name, maxAgentsNumber},
          onCompleted: (response, errors) => {
            if (errors == null) {
              // I expect the pool to appear with the WS message
              clearFields()

              if (response?.createAgentPool?.agentPool != null) {
                navigate(getAgentPoolHref(toAgentPoolId(response.createAgentPool.agentPool.rawId)))
              }
              resolve(response)
            } else {
              errors.forEach(error => alert.error(error.message))
              reject()
            }
          },
          onError: error => {
            alert.error(error.message)
            reject()
          },
        })
      })
    }

    return new Promise((resolve, reject) => {
      commitUpdate({
        variables: {rawId: agentPool.rawId, name, maxAgentsNumber},
        onCompleted: (response, errors) => {
          if (errors == null) {
            // I expect the pool to be updated with the WS message
            clearFields()
            resolve(response)
          } else {
            errors.forEach(error => alert.error(error.message))
            reject()
          }
        },
        onError: error => {
          alert.error(error.message)
          reject()
        },
      })
    })
  }, [
    clearErrors,
    name,
    agentPools,
    maxAgents,
    agentPool,
    commitCreate,
    clearFields,
    navigate,
    commitUpdate,
  ])
  const handleClose = React.useCallback(() => {
    clearErrors()
    clearFields()
    onClose()
  }, [clearErrors, clearFields, onClose])
  const handleNameChange = React.useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => setName(e.currentTarget.value),
    [setName],
  )
  const handleMaxAgentsChange = React.useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => setMaxAgents(e.currentTarget.value),
    [setMaxAgents],
  )
  const handleInputKeyDown = React.useCallback((e: React.KeyboardEvent<any>) => {
    // This is done to prevent the sidebar from catching key press
    // TODO: ideally, the sidebar should filter these events itself
    if (e.key !== 'Escape' && e.key !== 'Enter') {
      e.stopPropagation()
    }
  }, [])
  return (
    <CommonForm
      onSubmit={handleSubmit}
      onSuccess={onUpdate}
      onClose={handleClose}
      content={
        <>
          <Input
            autoFocus={agentPool == null}
            className={styles.name}
            label="Name"
            placeholder="Agent pool name"
            value={name}
            onChange={handleNameChange}
            onKeyDown={handleInputKeyDown}
            autoComplete="off"
            {...(nameError !== ''
              ? {
                  error: nameError,
                }
              : null)}
            maxLength={MAX_AGENT_POOL_NAME_LENGTH}
          />
          <Input
            className={styles.maxAgents}
            inputClassName={classNames(styles.maxAgentsInput, 'submit-on-cmd-enter')}
            label="Maximum agents"
            placeholder="Unlimited"
            value={maxAgents}
            onChange={handleMaxAgentsChange}
            onKeyDown={handleInputKeyDown}
            type="number"
            autoComplete="off"
            {...(maxAgentsError !== ''
              ? {
                  error: maxAgentsError,
                }
              : null)}
          />
        </>
      }
      extraButtons={
        agentPool == null ? null : (
          <DeleteAgentPoolButton agentPoolRawId={agentPool.rawId} onRemove={onRemove} />
        )
      }
      submitButtonContent={agentPool == null ? 'Create pool' : 'Update pool'}
      formRef={formRef}
    />
  )
}
